import React, { useLayoutEffect, useState, useRef, useEffect } from "react";
import axios from "axios";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Login from "../../screens/Login/components/Login";

// Using your Design Specs
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const ModalCard = styled.div`
  background-color: #f5f5f5;
  border-radius: 8px;
  width: 90%;
  max-width: 450px;
  box-shadow: -15px 15px 15px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 74, 35, 0.1);
`;

const DismissLink = styled.button`
  background: none;
  border: none;
  color: rgba(0, 74, 35, 0.6);
  cursor: pointer;
  font-size: 0.8rem;
  margin-top: 16px;
  padding: 0;
  text-decoration: underline;
  display: block;
  width: 100%;
  text-align: center;

  &:hover {
    color: rgba(0, 74, 35, 1);
  }
`;

const SessionManager = ({ children, modal }) => {
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const failedQueue = useRef([]);
  const navigate = useNavigate();

  // Live ref to userInfo so the interceptor closure (registered once) can read
  // the latest value. A 401 only counts as a "session expiry" when the user
  // was actually logged in — otherwise it's just an unauthenticated request
  // (e.g. NavBar's data-fetching effects firing on the /login screen) and
  // setting isSessionExpired here would pollute state and pop the modal as
  // soon as the user navigates to a route where modalTracker() is true.
  const userInfo = useSelector((state) => state.userLogin.userInfo);
  const userInfoRef = useRef(userInfo);
  useEffect(() => {
    userInfoRef.current = userInfo;
  }, [userInfo]);

  // Cleanup shared by the explicit modal-success path and the
  // userInfo-transition effect below. Idempotent: safe to call when the queue
  // is empty or the flag is already false.
  const flushAndClear = () => {
    failedQueue.current.forEach((prom) => {
      axios(prom.config).then(prom.resolve).catch(prom.reject);
    });
    failedQueue.current = [];
    setIsSessionExpired(false);
  };

  // Self-heal: any successful login (modal-driven OR normal /login flow) sets
  // a fresh userInfo in Redux. If isSessionExpired silently flipped to true
  // earlier (e.g. a 401 fired on /login where modalTracker() hides the modal),
  // we'd otherwise carry that stale flag onto /today and pop the modal there.
  // Treat any transition into a new userInfo._id as proof the session is good.
  const prevUserIdRef = useRef(userInfo?._id ?? null);
  useEffect(() => {
    const prev = prevUserIdRef.current;
    const curr = userInfo?._id ?? null;
    if (curr && curr !== prev) {
      flushAndClear();
    }
    prevUserIdRef.current = curr;
  }, [userInfo]);

  useLayoutEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Catch 401s that haven't been retried yet — but only when there was
        // an active session to begin with.
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          userInfoRef.current
        ) {
          originalRequest._retry = true; // Mark before queuing to prevent re-queuing
          setIsSessionExpired(true);

          // Pause the request and save it to the list
          return new Promise((resolve, reject) => {
            failedQueue.current.push({
              resolve,
              reject,
              config: originalRequest,
            });
          });
        }
        return Promise.reject(error);
      },
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  // Called when the User successfully enters email/pass in the modal.
  // The userInfo-transition effect above will also fire on USER_LOGIN_SUCCESS,
  // so this is belt-and-braces — kept as the explicit callback Login expects.
  const handleReAuthSuccess = () => {
    flushAndClear();
  };

  // Called when the user chooses to navigate to login instead of re-authing in-place
  const handleDismiss = () => {
    failedQueue.current.forEach((prom) => {
      prom.reject(new Error("Session expired — user redirected to login"));
    });
    failedQueue.current = [];
    setIsSessionExpired(false);
    navigate("/login");
  };

  return (
    <>
      {children}
      {isSessionExpired && modal && (
        <Overlay>
          <ModalCard>
            <Login isModal={true} onSuccess={handleReAuthSuccess} />
            <DismissLink onClick={handleDismiss}>
              Go to login page instead
            </DismissLink>
          </ModalCard>
        </Overlay>
      )}
    </>
  );
};

export default SessionManager;
